import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { stats, testimonials } from '@/mocks/data';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = fd.get('phone_alt') as string;

    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      setFormData({ name: '', email: '', message: '' });
      return;
    }

    fd.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rhh66e3oq4jqumu1f0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText;
        setFormStatus('error');
        setFormError(serverMsg || '发送失败，请稍后重试');
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于撸啊撸攻略大全',
    description: '撸啊撸攻略大全是一个专注于英雄联盟攻略内容创作与分享的平台，致力于为玩家提供最专业、最系统的游戏攻略。',
    inLanguage: 'zh-CN',
    about: {
      '@type': 'Organization',
      name: '撸啊撸攻略大全',
      description: '专注于英雄联盟攻略内容创作与分享的平台。',
      foundingDate: '2024',
    },
  };

  return (
    <>
      <Navbar />
      <main className="pt-16 bg-background-50">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutJsonLd) }}
        />

        <section className="bg-background-100 py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
            <div className="text-center mb-6">
              <h1 className="font-heading text-2xl md:text-4xl text-foreground-950 mb-4">
                关于撸啊撸攻略大全
              </h1>
              <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
                我们是一群热爱英雄联盟的资深玩家和内容创作者，致力于为每一位召唤师提供最专业、最系统的撸啊撸攻略。
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="font-heading text-2xl md:text-3xl text-primary-500 mb-1">{stat.value}</div>
                  <div className="text-sm text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-4">品牌故事</h2>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  撸啊撸攻略大全成立于2024年，由一群热爱英雄联盟的资深玩家共同创建。我们深知从青铜到王者的路上有多少艰辛——每个召唤师都可能经历过卡段位、对线被压制、团战总是输的困境。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  于是我们决定把自己多年的游戏经验和心得整理成系统化的攻略内容，帮助更多撸啊撸玩家找到正确的提升路径。我们的撸啊撸英雄攻略覆盖了160+位英雄的详细玩法解析，从技能机制到连招技巧，从出装推荐到对线思路，每一个细节都经过实战验证。
                </p>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  我们坚信，只要方法正确，任何人都可以从青铜一路打到王者。撸啊撸段位提升不是玄学，而是科学——我们有完整的训练方法论、版本跟进机制和大量实战案例支撑。目前我们的内容每月服务超过50万读者，好评率高达98%。
                </p>
              </div>

              <div>
                <h2 className="font-heading text-xl text-foreground-950 mb-4">核心价值</h2>
                <div className="space-y-4">
                  {[
                    { icon: 'ri-book-open-line', title: '专业系统', desc: '所有攻略经过实战验证，确保内容的准确性和实用性，覆盖从基础到进阶的完整知识体系。' },
                    { icon: 'ri-refresh-line', title: '版本跟进', desc: '每个版本更新后第一时间更新攻略内容，确保撸啊撸版本强势英雄和出装推荐始终与当前版本同步。' },
                    { icon: 'ri-user-heart-line', title: '玩家为本', desc: '从玩家的实际需求出发，撸啊撸新手教程和进阶攻略并重，让不同水平的召唤师都能找到适合自己的内容。' },
                    { icon: 'ri-team-line', title: '社区驱动', desc: '鼓励玩家交流和反馈，持续优化内容质量，打造最有活力的撸啊撸攻略社区。' },
                  ].map((item) => (
                    <div key={item.title} className="flex gap-4 p-4 rounded-xl bg-background-100 border border-background-200/70">
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                        <i className={`${item.icon} text-lg`}></i>
                      </div>
                      <div>
                        <h3 className="font-heading text-sm text-foreground-950 mb-1">{item.title}</h3>
                        <p className="text-xs text-foreground-600 leading-relaxed">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl text-foreground-950 text-center mb-3">
              玩家口碑
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-10">
              来自玩家们对撸啊撸攻略大全的真实评价
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((t) => (
                <div key={t.name} className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm text-primary-400"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    &ldquo;{t.text}&rdquo;
                  </p>
                  <p className="text-xs text-foreground-500 font-medium">{t.name}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-16 md:py-20 bg-background-50">
          <div className="max-w-lg mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl text-foreground-950 text-center mb-3">
              联系我们
            </h2>
            <p className="text-sm text-foreground-600 text-center mb-8">
              有任何问题或建议？欢迎随时与我们联系
            </p>

            {formStatus === 'success' ? (
              <div className="text-center p-8 rounded-xl bg-accent-100/50 border border-accent-200/50">
                <div className="w-14 h-14 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-500 text-background-50">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <h3 className="font-heading text-lg text-foreground-950 mb-2">消息已发送！</h3>
                <p className="text-sm text-foreground-600">感谢你的留言，我们会尽快回复</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-2">
                    你的称呼
                  </label>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-300 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入你的称呼"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-2">
                    邮箱地址
                  </label>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-300 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                    placeholder="请输入你的邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-2">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 rounded-lg bg-background-100 border border-background-300 text-foreground-950 text-sm focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入你想说的内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1 text-right">{formData.message.length}/500</p>
                </div>
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <button
                  type="submit"
                  className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  发送留言
                </button>
              </form>
            )}

            {formStatus === 'error' && (
              <p className="mt-4 text-sm text-accent-600 text-center">{formError}</p>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}